import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { InputField } from 'src/components';
import { actionColumnStyles } from 'src/components/Common/FixedActionColumn';
import { config } from 'src/config';
import { useAppSelector } from 'src/rtk/app/hook';
import { useChangeCurrentSessionMutation, useGetSlotApprovalByAdminMutation } from 'src/rtk/features/courseApproval/courseApprovalApi';
import { useGetRegInfoQuery } from 'src/rtk/features/registration-approval/registrationApprovalApi';
import * as Yup from 'yup';
import loader from '../../assets/images/loader.gif';
import avatar from '../../assets/images/users/default-avatar.png';

interface Props {
  isOpen: boolean;
  toggle: any;
  id: string;
  info: any;
}

const initialValues: RegApprove = {
  registration_no: '',
  status: '',
  message: '',
  type: '',
  registration_type: '',
};

const SlotApprovalViewModal = (props: Props) => {
  const { isOpen, toggle, id, info } = props;
  const [showApproveStatus, setShowApproveStatus] = useState<boolean>(false);
  const [showAttachment, setShowAttachment] = useState<boolean>(false);
  const [attachmentInfo, setAttachmentInfo] = useState<any>({});
  const [slotId, setSlotId] = useState<string>('');
  const buttonRef = useRef<any>();

  const [getSlotApprovalByAdmin, { isSuccess, isError, error, isLoading: isApprovalLoading }] = useGetSlotApprovalByAdminMutation();
  const [changeCurrentSession, { isLoading: isChangeSessionLoading }] = useChangeCurrentSessionMutation();
  const [showCurrentSessionModal, setShowCurrentSessionModal] = useState<boolean>(false);
  const [selectedSlotForCurrent, setSelectedSlotForCurrent] = useState<any>(null);

  const err = error as CustomError;

  const { data, isLoading } = useGetRegInfoQuery(id);
  const userInfo = data?.data || ({} as RegInfo);
  const authUser: any = useAppSelector((state) => state.auth.menu_permissions);

  const {
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    handleChange,
    getFieldProps,
    touched,
    errors,
  } = useFormik({
    initialValues,
    validationSchema: Yup.object({
      message: Yup.string().when('status', (status, schema) => {
        if (status === 'Correction') {
          return schema.required('Correction note is Required!');
        }
        return schema;
      }),
    }),
    onSubmit: (values) => {

      buttonRef.current.disabled = true;
      const url = `${slotId}?approve_type=${values.status}&note=${values.message}`;
      getSlotApprovalByAdmin(url);

    },
  });


  useEffect(() => {
    if (isSuccess) {
      toast.success('Approval Status Updated Successfully!');
      buttonRef.current.disabled = false;
      setShowApproveStatus(false);
    }
    if (isError) {
      toast.error('Something went wrong');
      buttonRef.current.disabled = false;
    }
  }, [isSuccess, isError]);

  return (
    <Modal
      isOpen={isOpen && !isLoading}
      backdrop={'static'}
      toggle={toggle}
      size="xl"
    >
      <ModalHeader toggle={toggle}>Slot Info</ModalHeader>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <ModalBody>
          {isLoading || isApprovalLoading ? (
            <div className="overlay">
              <img src={loader} alt="Loading" height={100} width={300} />
            </div>
          ) : null}
          <div>
            <h6 className="block-heading"> Personal Information</h6>
            <div className="row">
              <div className="col-md-9">
                <div className="row">
                  <div className="col-md-12">
                    <table className="table table-bordered table-striped table-sm">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Name: </strong>
                            {userInfo?.personal_info?.full_name}
                          </td>
                          <td>
                            {userInfo?.personal_info?.spouse_name && (
                              <div>
                                <strong>Spouse Name: </strong>
                                {userInfo?.personal_info?.spouse_name ?? 'N/A'}
                              </div>
                            )}

                            {userInfo?.personal_info?.fathers_name && (
                              <div>
                                <strong>Father's Name: </strong>
                                {userInfo?.personal_info?.fathers_name ?? 'N/A'}
                              </div>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Mother's Name: </strong>
                            {userInfo?.personal_info?.mothers_name ?? 'N/A'}
                          </td>
                          <td>
                            <strong>Date of Birth: </strong>
                            {userInfo?.personal_info?.date_of_birth ?? 'N/A'}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>NID: </strong>
                            {userInfo?.personal_info?.national_id ?? 'N/A'}
                          </td>

                          <td>
                            <strong>Mobile Number: </strong>
                            {userInfo?.personal_info?.mobile ?? 'N/A'}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Email: </strong>
                            {userInfo?.personal_info?.email ?? 'N/A'}
                          </td>

                          <td>
                            <strong>Alternative Number: </strong>
                            {userInfo?.personal_info?.telephone ?? 'N/A'}
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Subject: </strong>
                            {userInfo?.subject?.subject_name ?? 'N/A'}
                          </td>
                          <td>
                            <strong>Passing Session: </strong>
                            {info?.profile_info?.fcps_part_one_passing_session ? info?.profile_info?.fcps_part_one_passing_session + ' - ' + info?.profile_info?.fcps_part_one_passing_year : 'N/A'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="img-fluid img-rounded text-center">
                  <img
                    src={
                      userInfo?.personal_info?.picture
                        ? `${config?.APP_URL}${userInfo?.personal_info?.picture}`
                        : avatar
                    }
                    width="120"
                    className="img-thumbnail"
                  />
                </div>
              </div>
            </div>
            <h6 className="block-heading"> Supervisor Information</h6>
            <div className="row">
              <div className="col-md-9">
                <div className="row">
                  <div className="col-md-12">
                    <table className="table table-bordered table-striped table-sm">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Name: </strong>
                            {info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.name}
                          </td>

                          <td>
                            <strong>BMDC Reg. No.: </strong>
                            {info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.bmdc_reg_no ?? 'N/A'}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Designation: </strong>
                            {info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.designation ?? 'N/A'}
                          </td>

                          <td>
                            <strong>Mobile Number: </strong>
                            {info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.mobile ?? 'N/A'}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Institute: </strong>
                            {info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.institute_name ?? 'N/A'}
                          </td>
                          <td>
                            <strong>Email: </strong>
                            {info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.email ?? 'N/A'}
                          </td>
                        </tr>

                        <tr>
                          <td>
                            <strong>Department: </strong>
                            {info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.department_name ?? 'N/A'}
                          </td>
                          <td>
                            <strong>Satus: </strong>
                            {info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisors_approve_status}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="img-fluid img-rounded text-center">
                  <img
                    src={
                      info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.profile_picture
                        ? `${config?.APP_URL}${info?.profile_info?.unapproved_current_session_and_supervisor_info?.supervisor?.profile_picture}`
                        : avatar
                    }
                    width="120"
                    className="img-thumbnail"
                  />
                </div>
              </div>
            </div>
            <div className='row'>
              <div className='col table-responsive'>
                <h6 className='block-heading'>Slot Information</h6>
                <table className='table table-sm table-striped'>
                  <thead>
                    <tr>
                      <th>Sl No</th>
                      <th>Program Type</th>
                      <th>Slot</th>
                      <th>Duration</th>
                      <th>Institute</th>
                      <th>Department</th>
                      <th>Supervisor</th>
                      <th>Institute Category</th>
                      <th>Trainee Category</th>
                      <th>Involvement in Income</th>
                      <th>Supervisor Approval Status</th>
                      <th>Admin Approval Status</th>
                      <th style={actionColumnStyles}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info?.slot_info?.map((item: any, index: number) => (
                      <tr key={item.id}>
                        <td><strong>{++index}</strong></td>
                        <td>{item?.exam_training_info?.training_type === 'Advance' ? 'Specialty' : item?.exam_training_info?.training_type}</td>
                        <td>{item?.exam_training_info?.name}</td>
                        <td>{moment(item?.session_start_date).format('DD/MMM/YYYY')}{'-'}{moment(item?.session_end_date).format('DD/MMM/YYYY')}</td>
                        <td>{item?.supervisor?.institute_name}</td>
                        <td>{item?.supervisor?.department_name}</td>
                        <td>{item?.supervisor?.name}</td>
                        <td>{item?.training_institute_type ?? 'N/A'}</td>
                        <td>{item?.trainee_category ?? 'N/A'}</td>
                        <td>{item?.income_category ?? 'N/A'}</td>
                        <td>{
                          item?.supervisors_approve_status === 'Approve' ?
                            <div className="badge badge-soft-success font-size-12">
                              {'Approved'}
                            </div>
                            :
                            <div className="badge badge-soft-info font-size-12">
                              {item?.supervisors_approve_status}
                            </div>
                        }</td>
                        <td>{
                          item?.admin_approval_status === 'Approve' ?
                            <>
                              <div className="badge badge-soft-success font-size-12">
                                {'Approved'}
                              </div>
                            </>
                            :
                            <>
                              <div className="badge badge-soft-info font-size-12">
                                {item?.admin_approval_status}
                              </div>
                            </>
                        }</td>
                        <td style={actionColumnStyles}>
                          <div className='d-flex gap-1'>
                            {(item?.joining_letter || item?.release_order || item?.office_order) ?
                              <Button
                                className="btn btn-sm"
                                outline
                                color='success'
                                onClick={() => {
                                  setShowAttachment(true);
                                  setAttachmentInfo({
                                    joining_letter: item?.joining_letter,
                                    release_order: item?.release_order,
                                    office_order: item?.office_order
                                  });
                                }}
                              >
                                Attachment
                              </Button> : null
                            }

                            <Button
                              className="btn btn-sm"
                              color='primary'
                              disabled={item?.admin_approval_status === 'Approve' || item?.supervisors_approve_status !== 'Approve'}
                              onClick={() => {
                                setShowApproveStatus(!showApproveStatus);
                                setSlotId(item?.id);
                              }}
                            >
                              Approve
                            </Button>

                            {authUser?.includes('change-current-session') && (
                              item?.current_status === true ? (
                                <div className="badge badge-soft-success font-size-12 d-inline-flex align-items-center">
                                  <i className="bx bx-check-circle me-1" />
                                  {'Current'}
                                </div>
                              ) : (
                                <Button
                                  className="btn btn-sm"
                                  color='warning'
                                  outline
                                  title="Make Current Session"
                                  onClick={() => {
                                    setSelectedSlotForCurrent(item);
                                    setShowCurrentSessionModal(true);
                                  }}
                                >
                                  <i className="bx bx-edit" />
                                </Button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <Row>
            {showApproveStatus ?
              <>
                <div className="col-md-6 col-sm-12">
                  <h6 className="block-heading">Slot Approval</h6>

                  <FormGroup check inline className="mb-3 ms-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="profile_approval_status"
                      id="profile-approve"
                      checked={values.status === 'Approve'}
                      onChange={(e) => {
                        setFieldValue('status', 'Approve');
                      }}
                    />
                    <label
                      className="form-check-label text-success"
                      htmlFor="profile-approve"
                    >
                      Approve
                    </label>
                  </FormGroup>
                  <FormGroup check inline className="mb-3 ">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="profile_approval_status"
                      checked={values.status === 'Reject'}
                      id="reject"
                      onChange={() => {
                        setFieldValue('status', 'Reject');
                      }}
                    />
                    <label
                      className="form-check-label text-danger"
                      htmlFor="reject"
                    >
                      Reject
                    </label>
                  </FormGroup>
                  {/* <FormGroup check inline className="mb-3 ">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="profile_approval_status"
                      checked={values.status === 'Correction'}
                      id="correction"
                      onChange={() => {
                        setFieldValue('status', 'Correction');
                      }}
                    />
                    <label
                      className="form-check-label text-info"
                      htmlFor="correction"
                    >
                      Correction
                    </label>
                  </FormGroup> */}

                  {errors.status && touched.status ? (
                    <div className="text-danger">{errors.status}</div>
                  ) : null}

                  <div>
                    <Label for="message">{values.status}&nbsp;Note</Label>
                    <InputField
                      type="textarea"
                      placeholder="Enter.."
                      invalid={{ errors, touched }}
                      {...getFieldProps('message')}
                    />
                  </div>
                </div>
              </> : null
            }

          </Row>
        </ModalBody>

        <ModalFooter>
          <div className="d-flex gap-2 ">
            {showApproveStatus ?
              <button
                type="submit"
                className="btn btn-primary"
                ref={buttonRef}
              >
                Save
              </button>
              : null
            }
            <Button color="danger" onClick={toggle}>
              Close
            </Button>
          </div>
        </ModalFooter>
      </Form>
      <Modal
        isOpen={showAttachment}
        backdrop={'static'}
        size="md"
      >
        <ModalHeader toggle={() => setShowAttachment(!showAttachment)}>Attachment</ModalHeader>
        <ModalBody>
          <div>
            <table className='table table-sm'>
              <thead>
                <tr>
                  <th>
                    Sl No
                  </th>
                  <th>Name Of The Document</th>
                  <th>File Preview</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Joining Letter</td>
                  <td>
                    <a href={config.APP_URL + attachmentInfo?.joining_letter} target="_blank" rel="noopener noreferrer">
                      View
                    </a>
                  </td>
                </tr>
                {attachmentInfo?.release_order &&
                  <tr>
                    <td>2</td>
                    <td>Release Order</td>
                    <td>
                      <a href={config.APP_URL + attachmentInfo?.release_order} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    </td>
                  </tr>}
                {attachmentInfo?.office_order &&
                  <tr>
                    <td>3</td>
                    <td>Office Order</td>
                    <td>
                      <a href={config.APP_URL + attachmentInfo?.office_order} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    </td>
                  </tr>}
              </tbody>

            </table>
          </div>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={showCurrentSessionModal}
        backdrop={'static'}
        toggle={() => setShowCurrentSessionModal(!showCurrentSessionModal)}
        size="md"
        centered
      >
        <ModalHeader toggle={() => setShowCurrentSessionModal(false)}>
          Change Current Session Confirmation
        </ModalHeader>
        <ModalBody>
          {isChangeSessionLoading ? (
            <div className="overlay">
              <img src={loader} alt="Loading" height={100} width={300} />
            </div>
          ) : null}
          <div className="text-center p-3">
            <div className="text-warning mb-3">
              <i className="bx bx-error-circle" style={{ fontSize: '3.5rem' }}></i>
            </div>
            <h5>Are you sure you want to make this slot current?</h5>
            <p className="text-muted mb-0 mt-2">
              Slot Name: <strong>{selectedSlotForCurrent?.exam_training_info?.name || selectedSlotForCurrent?.name || 'N/A'}</strong>
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            disabled={isChangeSessionLoading}
            onClick={async () => {
              try {
                const bmdc = info?.profile_info?.bmdc_registration_no;
                const sessionId = selectedSlotForCurrent?.id;
                const res: any = await changeCurrentSession({
                  bmdc: bmdc,
                  session_id: sessionId,
                }).unwrap();
                if (res?.success || res?.status === 200 || res?.status === 'success') {
                  toast.success(res?.message || 'Current session changed successfully!');
                } else {
                  toast.success('Current session changed successfully!');
                }

                setShowCurrentSessionModal(false);
              } catch (err: any) {
                toast.error(err?.data?.message || err?.message || 'Failed to change current session');
              }
            }}
          >
            Yes
          </Button>
          <Button
            color="secondary"
            disabled={isChangeSessionLoading}
            onClick={() => setShowCurrentSessionModal(false)}
          >
            No
          </Button>
        </ModalFooter>
      </Modal>
    </Modal>

  );
};

export default SlotApprovalViewModal;
